import { useCallback, useEffect, useState } from 'react'

import ConversationsApiService from '@/apis/conversationsApiService'
import useAnswerStreamContext from '@/hooks/useAnswerStreamContext'
import type { Conversation } from '@/types/chat'

export default function useConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { subscribe } = useAnswerStreamContext()

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const data = await ConversationsApiService.index(signal)
      setConversations(data.conversations)
    } catch (err) {
      if ((err as { name?: string }).name === 'CanceledError') return
      // Other errors handled by global 401 interceptor
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  useEffect(
    () =>
      subscribe((event) => {
        if (event.type !== 'conversation_named') return
        setConversations((prev) =>
          prev.map((c) => (c.id === event.conversationId ? { ...c, name: event.name } : c)),
        )
      }),
    [subscribe],
  )

  return { conversations, isLoading, refetch: load }
}
